import * as React from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../../lib/stylingUtils';

type CarouselProps = {
  children: React.ReactNode[];
  initialIndex?: number;
  onSlideChange?: (index: number) => void;
  showIndicators?: boolean;
  hideArrows?: boolean;
  className?: string;
  arrowSize?: number;
  previousLabel?: string;
  nextLabel?: string;
  indicatorLabel?: (index: number) => string;
};

export const Carousel: React.FC<CarouselProps> = ({
  children,
  initialIndex = 0,
  onSlideChange,
  showIndicators = true,
  hideArrows = false,
  className,
  arrowSize = 12,
  previousLabel = 'Previous slide',
  nextLabel = 'Next slide',
  indicatorLabel = (index) => `Go to slide ${index + 1}`,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);

  const safeCurrentIndex = Math.min(
    currentIndex,
    Math.max(0, children.length - 1),
  );

  const handlePrevious = React.useCallback(() => {
    const newIndex =
      safeCurrentIndex === 0 ? children.length - 1 : safeCurrentIndex - 1;
    setCurrentIndex(newIndex);
    onSlideChange?.(newIndex);
  }, [safeCurrentIndex, children.length, onSlideChange]);

  const handleNext = React.useCallback(() => {
    const newIndex =
      safeCurrentIndex === children.length - 1 ? 0 : safeCurrentIndex + 1;
    setCurrentIndex(newIndex);
    onSlideChange?.(newIndex);
  }, [safeCurrentIndex, children.length, onSlideChange]);

  const handleIndicatorClick = React.useCallback(
    (index: number) => {
      setCurrentIndex(index);
      onSlideChange?.(index);
    },
    [onSlideChange],
  );

  // Reset index if initialIndex changes or clamp if out of bounds
  React.useLayoutEffect(() => {
    if (initialIndex >= 0 && initialIndex < children.length) {
      setCurrentIndex(initialIndex);
    } else if (children.length > 0) {
      setCurrentIndex(0);
    }
  }, [initialIndex, children.length]);

  if (children.length === 0) return null;

  return (
    <div className={cn('@Carousel', 'group relative h-full w-full', className)}>
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="flex h-full transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${safeCurrentIndex * 100}%)` }}
        >
          {children.map((child, index) => (
            <div key={index} className="h-full w-full flex-shrink-0">
              {child}
            </div>
          ))}
        </div>
      </div>

      {!hideArrows && children.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrevious}
            className="absolute top-1/2 left-2 -translate-y-1/2 cursor-pointer rounded-full bg-neutral/50 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-neutral/70"
            aria-label={previousLabel}
          >
            <ChevronLeft size={arrowSize} />
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer rounded-full bg-neutral/50 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-neutral/70"
            aria-label={nextLabel}
          >
            <ChevronRight size={arrowSize} />
          </button>
        </>
      )}

      {showIndicators && children.length > 1 && (
        <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-2">
          {children.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleIndicatorClick(index)}
              className={cn(
                'size-2 rounded-full transition-all',
                safeCurrentIndex === index
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/75',
              )}
              aria-label={indicatorLabel(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
